// src/app/settings.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView,
  KeyboardAvoidingView, TouchableWithoutFeedback, Keyboard,
  Platform, TouchableOpacity, Modal, Image
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { IoPeople } from 'react-icons/io5'; // Changed from IoPersonAdd to IoPeople
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function Settings() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [userData, setUserData] = useState(null);      // live data (view mode)
  const [draft, setDraft] = useState(null);            // edit buffer
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userType, setUserType] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchUserData = async () => {
    try {
      const token = getToken();
      const userPermissionsString = localStorage.getItem('userPermissions');
      if (userPermissionsString) {
        const userPermissions = JSON.parse(userPermissionsString);
        setUserType(userPermissions.user_type);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData(response.data || {});
      setDraft(response.data || {});
      
      // Set logo preview if exists
      if (response.data?.businessLogo) {
        setLogoPreviewUrl(`${API_BASE_URL}/api/uploads/${response.data.businessLogo}`);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleEdit = () => {
    setDraft(userData);     // start from current values
    setIsEditing(true);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    console.log('File selected:', file.name, file.type, file.size);
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      window.alert('Please select a JPG or PNG image file.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      window.alert('Logo file size must be less than 5MB.');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload the logo immediately
    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Uploading to:', `${API_BASE_URL}/api/upload-logo`);
      
      const response = await axios.post(`${API_BASE_URL}/api/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Upload response:', response.data);
      
      if (response.data && response.data.success) {
        setDraft((p) => ({ ...p, businessLogo: response.data.file_path }));
        window.alert('Logo uploaded successfully!');
      } else {
        console.error('Unexpected response format:', response.data);
        window.alert('Upload completed but response was unexpected.');
      }
    } catch (error) {
      console.error('Error uploading logo:', error.response?.data || error.message);
      window.alert(`Failed to upload logo: ${error.response?.data?.detail || error.message}`);
      setLogoPreviewUrl(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      
      // Only include personal info that all users can edit
      const payload = {
        firstName: draft.firstName || '',
        lastName:  draft.lastName  || '',
        phone:     draft.phone     || '',
      };

      // Only add business fields if user is owner or admin
      if (userType === 'owner' || userType === 'admin') {
        payload.businessName = draft.businessName || '';
        payload.businessLogo = draft.businessLogo || '';
        payload.address = draft.address || '';
        payload.city = draft.city || '';
        payload.state = draft.state || '';
        payload.zip = draft.zip || '';
      }

      await axios.put(`${API_BASE_URL}/api/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIsEditing(false);
      // Refresh the page after successful save
      window.location.reload();
    } catch (error) {
      console.error('Error saving user data:', error);
      window.alert('Failed to save settings. Please try again.');
    }
  };

  const handleChange = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value === '' ? null : value }));
  };

  const handlePhoneChange = (value) => {
    // Only allow digits, spaces, dashes, parentheses, and plus sign
    const cleaned = value.replace(/[^\d\s\-\(\)\+]/g, '');
    setDraft((prev) => ({ ...prev, phone: cleaned === '' ? null : cleaned }));
  };

  const handleDelete = async () => {
    try {
      const token = getToken();
      const userPermissionsString = localStorage.getItem('userPermissions');
      let userId = null;
      
      if (userPermissionsString) {
        const userPermissions = JSON.parse(userPermissionsString);
        userId = userPermissions.userId;
      }
      
      if (!userId) {
        window.alert('Unable to determine user ID');
        return;
      }
      
      const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Always clear storage and go to login when deleting from settings
      localStorage.removeItem('userToken');
      localStorage.removeItem('userPermissions');
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.response?.status === 403) {
        window.alert(error.response.data.detail || 'You do not have permission to delete this account');
      } else {
        window.alert('Failed to delete account. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setDraft(userData);  // revert edits
    setIsEditing(false);
    // Reset logo preview to original
    if (userData?.businessLogo) {
      setLogoPreviewUrl(`${API_BASE_URL}/api/uploads/${userData.businessLogo}`);
    } else {
      setLogoPreviewUrl(null);
    }
  };

  if (!userData) return <Text>Loading user data...</Text>;

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <ScrollView contentContainerStyle={{ flexGrow: 1, padding: 20 }} keyboardShouldPersistTaps="handled">
          <View style={styles.container}>
            <Text style={styles.header}>Settings</Text>
            
            {/* Hidden file input for web image selection */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onFilePicked}
            />
            
            {(userType === 'owner' || userType === 'admin') && (
              <TouchableOpacity
                style={styles.addUserIconButton}
                onPress={() => navigate('/users')}
              >
                <div style={{
                  backgroundColor: '#007BFF',
                  borderRadius: '5px',
                  width: 30,
                  height: 30,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: '8px'
                }}>
                  <IoPeople size={18} color="white" />
                </div>
                <Text style={styles.usersButtonText}>Users</Text>
              </TouchableOpacity>
            )}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>First Name:</Text>
              {isEditing ? (
                <input
                  style={styles.input}
                  value={draft.firstName || ''}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.firstName || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Last Name:</Text>
              {isEditing ? (
                <input
                  style={styles.input}
                  value={draft.lastName || ''}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.lastName || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.displayText}>{userData.email || 'Not Set'}</Text>
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Phone:</Text>
              {isEditing ? (
                <input
                  style={styles.input}
                  type="tel"
                  value={draft.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.phone || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Password:</Text>
              <TouchableOpacity 
                style={styles.changePasswordButton}
                onPress={() => navigate('/change-password')}
              >
                <Text style={styles.changePasswordText}>Change Password</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Business Name:</Text>
              {(isEditing && (userType === 'owner' || userType === 'admin')) ? (
                <input
                  style={styles.input}
                  value={draft.businessName || ''}
                  onChange={(e) => handleChange('businessName', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.businessName || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Business Logo:</Text>
              {(userType === 'owner' || userType === 'admin') ? (
                <>
                  {isEditing ? (
                    <>
                      <TouchableOpacity 
                        style={styles.logoButton} 
                        onPress={openFilePicker}
                        disabled={uploadingLogo}
                      >
                        <Text style={styles.logoButtonText}>
                          {uploadingLogo ? 'Uploading...' : draft.businessLogo ? 'Change Logo' : 'Upload Logo'}
                        </Text>
                      </TouchableOpacity>
                      {logoPreviewUrl && (
                        <View style={styles.logoPreviewContainer}>
                          <Image source={{ uri: logoPreviewUrl }} style={styles.logoPreview} />
                          <Text style={styles.logoPreviewText}>Logo preview</Text>
                        </View>
                      )}
                      {draft.businessLogo && (
                        <TouchableOpacity 
                          onPress={() => {
                            setDraft((p) => ({ ...p, businessLogo: '' }));
                            setLogoPreviewUrl(null);
                          }}
                        >
                          <Text style={styles.removeLogoButtonText}>Remove Logo</Text>
                        </TouchableOpacity>
                      )}
                    </>
                  ) : (
                    <>
                      {userData.businessLogo ? (
                        <View style={styles.logoPreviewContainer}>
                          <Image 
                            source={{ uri: `${API_BASE_URL}/api/uploads/${userData.businessLogo}` }} 
                            style={styles.logoPreview} 
                          />
                        </View>
                      ) : (
                        <Text style={styles.displayText}>Not Set</Text>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {userData.businessLogo ? (
                    <View style={styles.logoPreviewContainer}>
                      <Image 
                        source={{ uri: `${API_BASE_URL}/api/uploads/${userData.businessLogo}` }} 
                        style={styles.logoPreview} 
                      />
                    </View>
                  ) : (
                    <Text style={styles.displayText}>Not Set</Text>
                  )}
                </>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Address:</Text>
              {(isEditing && (userType === 'owner' || userType === 'admin')) ? (
                <input
                  style={styles.input}
                  value={draft.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.address || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>City:</Text>
              {(isEditing && (userType === 'owner' || userType === 'admin')) ? (
                <input
                  style={styles.input}
                  value={draft.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.city || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>State:</Text>
              {(isEditing && (userType === 'owner' || userType === 'admin')) ? (
                <input
                  style={styles.input}
                  value={draft.state || ''}
                  onChange={(e) => handleChange('state', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.state || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Zip:</Text>
              {(isEditing && (userType === 'owner' || userType === 'admin')) ? (
                <input
                  style={styles.input}
                  value={draft.zip || ''}
                  onChange={(e) => handleChange('zip', e.target.value)}
                />
              ) : (
                <Text style={styles.displayText}>{userData.zip || 'Not Set'}</Text>
              )}
            </View>

            <View style={styles.buttonContainer}>
              {!isEditing ? (
                <>
                  <TouchableOpacity style={[styles.button, styles.editButton]} onPress={handleEdit}>
                    <Text style={styles.buttonText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.button, styles.deleteButton]} onPress={() => setDeleteModalVisible(true)}>
                    <Text style={styles.buttonText}>Delete Account</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TouchableOpacity style={[styles.button, styles.saveButton]} onPress={handleSave}>
                    <Text style={styles.saveButtonText}>Save</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.button, styles.cancelButton]} onPress={handleCancel}>
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>

      <Modal visible={isDeleteModalVisible} transparent animationType="slide" onRequestClose={() => setDeleteModalVisible(false)}>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalText}>
              Are you sure you want to delete your account? All of your saved data will be lost.
            </Text>
            <View style={styles.modalButtonContainer}>
              <TouchableOpacity style={[styles.button, { backgroundColor: 'red' }]} onPress={handleDelete}>
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, { backgroundColor: 'gray' }]} onPress={() => setDeleteModalVisible(false)}>
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingBottom: 100, backgroundColor: '#fff' },
  header: { fontSize: 24, fontWeight: 'bold', marginBottom: 20, textDecorationLine: 'underline' },
  fieldContainer: { marginBottom: 15 },
  label: {
    fontWeight: 'bold', marginTop: 15, fontSize: 20, color: '#333',
    borderBottomWidth: 1, borderBottomColor: '#ddd', paddingBottom: 5,
  },
  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4, marginTop: 5, backgroundColor: '#fff', elevation: 1,
  },
  inputNoBorder: { borderWidth: 0 },
  buttonContainer: { marginTop: 30 },
  button: { flex: 1, padding: 10, borderRadius: 5, alignItems: 'center', marginHorizontal: 5 },
  buttonText: { color: 'white', fontWeight: 'bold' },
  editButton: { backgroundColor: 'blue', marginTop: 10 },
  deleteButton: {
    backgroundColor: '#FF3B30', padding: 10, marginTop: 15, borderRadius: 5, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cancelButton: { backgroundColor: '#FF3B30', padding: 10, borderRadius: 8, alignItems: 'center', marginTop: 20 },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  // Modal styles
  modalContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)' },
  modalContent: { width: '80%', backgroundColor: '#fff', padding: 20, borderRadius: 10, alignItems: 'center' },
  modalText: { fontSize: 18, marginBottom: 20, textAlign: 'center' },
  modalButtonContainer: { flexDirection: 'row', justifyContent: 'space-between', width: '100%' },

  // Add User button styles
  addUserIconButton: {
    position: 'absolute', 
    padding: '15px', 
    top: 10, 
    right: 10, 
    backgroundColor: 'transparent', 
    flexDirection: 'row', 
    alignItems: 'center',
  },
  usersButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007BFF',
  },

  // Change Password button styles
  changePasswordButton: {
    backgroundColor: '#007BFF', 
    paddingVertical: 6, 
    paddingHorizontal: 12, 
    borderRadius: 4, 
    alignItems: 'center', 
    marginTop: 5,
    alignSelf: 'flex-start'
  },
  changePasswordText: { 
    color: '#fff', 
    fontSize: 12, 
    fontWeight: 'bold' 
  },
  logoButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  logoButtonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  logoPreviewContainer: {
    alignItems: 'center',
    marginTop: 10,
  },
  logoPreview: {
    width: 100,
    height: 100,
    borderRadius: 10,
  },
  logoPreviewText: {
    marginTop: 5,
    color: '#6B7280',
  },
  removeLogoButton: {
    backgroundColor: '#FF3B30',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  removeLogoButtonText: {
    color: '#FF3B30',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
    textAlign: 'right'
  },
  displayText: {
    color: '#333',
    marginTop: 5,
  },
});
